import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tbody,
  Spacer,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useCallback } from 'react';

import { FormulaEngine } from '@finley-technologies/finley-types';

import { DeleteArrayPropertyButton } from '../../delete/DeleteArrayPropertyButton';
import { isThresholdValueIntermediateField } from '../covenantValue/thresholdValueUtils';
import { EditIntermediateValueRow } from './EditIntermediateValueRow';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';
import { useDraggableRow } from '~/packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from '~/packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from '~/packages/common-frontend/components/DragAndDropTable/ui';
import { FormulaContainer } from '~/packages/common-frontend/components/FormulaContainer';
import {
  AddTextButton,
  CopyIconButton,
  EditIconButton,
} from '~/packages/common-frontend/components/IconButton';
import {
  CreditAgreement,
  getIterationKey,
} from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from '~/packages/creditAgreements/ui/types';

const createIntermediateFieldShell = (
  idx: number,
): CreditAgreement.Config.CommonIntermediateField.IntermediateField => ({
  field: `fieldName${idx}`,
  config: {
    formula: 'placeHolder',
    type: 'formula',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
});

const RequiredEditableFields = [
  ...CreditAgreement.Config.PortfolioCovenant.RequiredIntermediateFieldIterable,
  ...CreditAgreement.Config.FinancialCovenant.RequiredIntermediateFieldIterable,
].filter(
  // This is a mistake if added (available by default), so need to be able to delete
  (field) => field !== 'triggerDate',
);

type IntermediateFieldViewProps = {
  intermediateField: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
  wipConfig?: WipConfig;
  configPropertyKey: string;
  basePropertyKey: string;
  fullPropertyKey: string;
  idx: number;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  id: string;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
};
const IntermediateFieldView = ({
  wipConfig,
  intermediateField,
  basePropertyKey,
  configPropertyKey,
  fullPropertyKey,
  idx,
  moveRow,
  isDragDisabled,
  id,
  onSave,
}: IntermediateFieldViewProps) => {
  const [isIntermediateValueEditOn, setIsIntermediateValueEditOn] = useState(
    false,
  );

  const duplicateIntermediateField = () => {
    const fields = _.cloneDeep(
      getIn(wipConfig?.formProps.values, basePropertyKey),
    );

    const copiedField = _.cloneDeep(intermediateField);
    copiedField.field = `${intermediateField.field}Copy`;

    fields.unshift(copiedField);

    wipConfig?.formProps.setFieldValue(basePropertyKey, fields);
  };

  const needToIncludeField = RequiredEditableFields.some(
    (field) => field === intermediateField.field,
  );

  if (!isIntermediateValueEditOn || !isDragDisabled) {
    return (
      <TrDraggableHandle
        index={idx}
        id={id}
        acceptedType="intermediateField"
        moveRow={moveRow}
        isDragDisabled={isDragDisabled}
      >
        <Td>{intermediateField.field}</Td>
        <Td whiteSpace="pre-wrap">
          <FormulaContainer>
            {String(intermediateField.config.formula)}
          </FormulaContainer>
        </Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && isDragDisabled && (
          <Td>
            <CopyIconButton
              ariaLabel="duplicate intermediate field"
              label="Duplicate Intermediate Field"
              onClick={duplicateIntermediateField}
            />
            <EditIconButton
              ariaLabel="edit intermediate field"
              label="Edit Intermediate Field"
              onClick={() => {
                setIsIntermediateValueEditOn(true);
              }}
            />
            {!needToIncludeField && (
              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={idx}
                useConfirmationModal
                saveOnDelete
              />
            )}
          </Td>
        )}
      </TrDraggableHandle>
    );
  }

  return (
    <EditIntermediateValueRow
      onCancel={() => setIsIntermediateValueEditOn(false)}
      onSave={onSave}
      intermediateField={intermediateField}
      isDragDisabled={isDragDisabled}
      moveRow={moveRow}
      idx={idx}
      id={id}
      operationMode={wipConfig?.operationMode}
      configPropertyKey={configPropertyKey}
      fullPropertyKey={fullPropertyKey}
    />
  );
};

type IntermediateFieldsViewProps = {
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  wipConfig?: WipConfig;
  intermediateFieldPropertyKey: string;
  configPropertyKey: string;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
};
export const IntermediateFieldsView = ({
  wipConfig,
  intermediateFieldPropertyKey,
  configPropertyKey,
  intermediateFields,
  onSave,
}: IntermediateFieldsViewProps) => {
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.CommonIntermediateField.IntermediateField>(
    { comparisonKey: 'field' },
  );
  const [isOrderLocked] = useOrderLockedState();
  const [intermediateFieldsState] = useRowState(intermediateFields);

  const onSaveOrder = useCallback(() => {
    wipConfig?.formProps.setFieldValue(
      intermediateFieldPropertyKey,
      intermediateFieldsState,
    );
  }, [intermediateFieldPropertyKey, intermediateFieldsState, wipConfig]);

  const addIntermediateField = (wc: WipConfig) => {
    const fields = _.cloneDeep(
      getIn(wc.formProps.values, intermediateFieldPropertyKey),
    );

    const field = createIntermediateFieldShell(fields.length);

    fields.unshift(field);

    wc.formProps.setFieldValue(intermediateFieldPropertyKey, fields);
  };

  return (
    <Flex>
      <Box width="100%">
        <Box mb="5">
          <Flex alignItems="center">
            {wipConfig?.isMutateAllowed(intermediateFieldPropertyKey) && (
              <>
                <LockOrderButton
                  onSaveOrder={onSaveOrder}
                  isDisabled={
                    !wipConfig.isMutateAllowed(intermediateFieldPropertyKey)
                  }
                />
                <RestoreOrderButton />
              </>
            )}
            <Text fontWeight="bold" fontSize="large" mr="2">
              Intermediate Values
            </Text>
            {wipConfig?.isMutateAllowed(intermediateFieldPropertyKey) && (
              <>
                <Spacer />
                {isOrderLocked && (
                  <AddTextButton
                    ariaLabel="add intermediate field"
                    label="Add Intermediate Field"
                    onClick={() => {
                      addIntermediateField(wipConfig);
                    }}
                  />
                )}
              </>
            )}
          </Flex>

          {wipConfig?.isMutateAllowed(intermediateFieldPropertyKey) && (
            <SaveOrderCallout />
          )}
        </Box>

        {intermediateFields.length === 0 && (
          <Text p="3" border="1px" borderColor="gray.700">
            There are currently no intermediate values.
          </Text>
        )}
        {intermediateFields.length > 0 && (
          <Box overflow="auto" maxHeight="50vh" borderWidth="1px">
            <Table>
              <Thead>
                <Tr position="sticky" top="0" zIndex={1}>
                  {wipConfig && !isOrderLocked && <Th w="10" />}
                  <Th w="30%">Field Name</Th>
                  <Th w="55%">Formula</Th>
                  {wipConfig && isOrderLocked && <Th />}
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  {!isOrderLocked && <Td />}
                  <Td>
                    <Flex alignItems="center">
                      <Text mr="8px">triggerDate</Text>
                      <CalculationTerminologiesPopover termKeyLabel="triggerDate" />
                    </Flex>
                  </Td>
                  <Td color={colors.black[60]}>
                    By default, the &apos;triggerDate&apos; intermediate field
                    is configured.
                  </Td>
                  {wipConfig && <Td />}
                </Tr>
                {intermediateFieldsState.map((intermediateField, idx) => {
                  const isOptionalIntermediateField =
                    !_.includes(
                      RequiredEditableFields,
                      intermediateField.field,
                    ) &&
                    !isThresholdValueIntermediateField(intermediateField.field);

                  if (isOptionalIntermediateField) {
                    const id = getIterationKey(intermediateField);
                    return (
                      <IntermediateFieldView
                        key={`${id}-${intermediateField.field}`}
                        configPropertyKey={configPropertyKey}
                        intermediateField={intermediateField}
                        wipConfig={wipConfig}
                        basePropertyKey={intermediateFieldPropertyKey}
                        fullPropertyKey={`${intermediateFieldPropertyKey}[${idx}]`}
                        idx={idx}
                        moveRow={moveRow}
                        isDragDisabled={isOrderLocked}
                        id={id}
                        onSave={onSave}
                      />
                    );
                  }

                  return null;
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
